'use client';

import * as React from 'react';
import Image from 'next/image';
import type { IconType } from 'react-icons';
import {
  TbArrowLeft,
  TbArrowRight,
  TbBook2,
  TbBuildingSkyscraper,
  TbListNumbers,
  TbSend,
  TbSwords,
  TbTicket,
  TbTrendingUp,
  TbUsers,
  TbUserSearch
} from 'react-icons/tb';

import { cn } from '@workspace/ui/lib/utils';

import { openDemoSection } from './pipeline-live-demo';
import { PLAY_SCREENS } from './pipeline-steps-section';

// Use cases as a carousel of real screens (Abu, Nov 1). The first view reads
// act, go deep, get known: outreach, person research, audience building., in place of the
// "How it works" story, so the page has one framework (the tabbed section)
// and one place for the product screens. Each card opens its page in the
// live demo. Screens and demo pages come from PLAY_SCREENS.
const USE_CASES_HEADER = {
  headline: 'What teams use Lumnis for'
} as const;

const USE_CASES: ReadonlyArray<{
  screen: keyof typeof PLAY_SCREENS;
  title: string;
  line: string;
  icon: IconType;
}> = [
  {
    screen: 'Top-account outreach',
    title: 'Top-account outreach',
    line: 'The right people at your top accounts, and messages on what they care about.',
    icon: TbSend
  },
  {
    screen: 'Person research',
    title: 'Person research',
    line: 'Who someone is, what they care about and who they engage with most.',
    icon: TbUserSearch
  },
  {
    screen: 'Audience building',
    title: 'Audience building',
    line: 'What your buyers care about, and the people who drive it.',
    icon: TbUsers
  },
  {
    screen: 'Scale your best-seller’s judgment',
    title: 'Your best rep’s playbook',
    line: 'Learns how your top reps work a deal and brings it to every rep.',
    icon: TbBook2
  },
  {
    screen: 'Account prioritization',
    title: 'Account prioritization',
    line: 'The accounts to act on this week, with why now and the next move.',
    icon: TbListNumbers
  },
  {
    screen: 'Conference outreach',
    title: 'Conference outreach',
    line: 'The attendees worth meeting, ranked, with the invitations drafted.',
    icon: TbTicket
  },
  {
    screen: 'Account progression',
    title: 'Account progression',
    line: 'Your open deals from the CRM, with what changed and who to bring in.',
    icon: TbBuildingSkyscraper
  },
  {
    screen: "What's trending",
    title: 'What’s trending',
    line: 'The topics your audience engages with each week, rising or falling.',
    icon: TbTrendingUp
  },
  {
    screen: 'Competitor traction',
    title: 'Competitor traction',
    line: 'What your competitors post about and how much of it lands.',
    icon: TbSwords
  }
];

export function PipelineUseCases(): React.JSX.Element {
  const track = React.useRef<HTMLUListElement>(null);
  const [edges, setEdges] = React.useState({ start: true, end: false });

  const updateEdges = React.useCallback(() => {
    const element = track.current;
    if (!element) return;
    setEdges({
      start: element.scrollLeft <= 4,
      end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 4
    });
  }, []);

  React.useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges]);

  // One arrow click moves one card.
  const step = (direction: 1 | -1): void => {
    const element = track.current;
    const card = element?.querySelector('li');
    if (!element || !card) return;
    element.scrollBy({
      left: direction * (card.getBoundingClientRect().width + 20),
      behavior: 'smooth'
    });
  };

  return (
    <section
      aria-labelledby="use-cases-title"
      className="w-full py-14 md:py-20"
    >
      <header className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <h2
          id="use-cases-title"
          className="display-h2 text-3xl text-[color:var(--ink)] [text-wrap:balance] md:text-4xl lg:text-5xl"
        >
          {USE_CASES_HEADER.headline}
        </h2>
      </header>

      <div className="mx-4 mt-10 sm:mx-6 lg:mx-[var(--landing-frame-inset,24px)]">
        <div className="mb-4 flex justify-end gap-2 px-5 md:px-8">
          {(
            [
              {
                direction: -1,
                label: 'Previous use case',
                Icon: TbArrowLeft,
                off: edges.start
              },
              {
                direction: 1,
                label: 'Next use case',
                Icon: TbArrowRight,
                off: edges.end
              }
            ] as const
          ).map(({ direction, label, Icon, off }) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              disabled={off}
              onClick={() => step(direction)}
              className="flex size-10 items-center justify-center rounded-full border border-[color:var(--hairline)] bg-[color:var(--paper)] text-[color:var(--ink-2)] transition-colors hover:bg-[color:var(--paper-2)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-darker)] disabled:opacity-40"
            >
              <Icon
                aria-hidden
                className="size-4"
              />
            </button>
          ))}
        </div>

        <ul
          ref={track}
          onScroll={updateEdges}
          className="flex snap-x snap-mandatory scroll-px-5 gap-5 overflow-x-auto scroll-smooth px-5 pb-2 md:scroll-px-8 md:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {USE_CASES.map((useCase) => {
            const screen = PLAY_SCREENS[useCase.screen];
            if (!screen) return null;
            const Icon = useCase.icon;
            return (
              <li
                key={useCase.title}
                className="w-[85%] shrink-0 snap-start sm:w-[420px] lg:w-[460px]"
              >
                <button
                  type="button"
                  onClick={() => openDemoSection(screen.demo)}
                  className="group block w-full text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[color:var(--accent-darker)]"
                >
                  <span className="lumnis-warm-panel block rounded-2xl p-4 sm:p-5">
                    <span className="relative block aspect-[8/5] overflow-hidden rounded-lg bg-[color:var(--paper)] shadow-[0_10px_30px_-18px_rgba(10,30,24,0.45)]">
                      <Image
                        src={screen.image}
                        alt={screen.alt}
                        fill
                        sizes="(min-width: 1024px) 420px, 85vw"
                        className="object-cover object-left-top transition-transform duration-500 ease-out group-hover:scale-[1.02] motion-reduce:transition-none"
                      />
                    </span>
                  </span>
                  <span className="mt-4 flex items-center gap-2 text-[18px] font-semibold text-[color:var(--ink)]">
                    <Icon
                      aria-hidden
                      className="size-5 shrink-0 text-[color:var(--accent-darker)]"
                    />
                    {useCase.title}
                  </span>
                  <span className="mt-1 block text-[15px] leading-relaxed text-[color:var(--ink-3)]">
                    {useCase.line}
                  </span>
                  <span
                    className={cn(
                      'mt-2 block text-[13px] font-medium text-[color:var(--accent-darker)]',
                      'underline-offset-4 group-hover:underline'
                    )}
                  >
                    Open in the live demo
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
